/**
 * Supplier HTML (descriptions, itineraries, inclusions, hotel info) is rich
 * text written in a third-party back office and is rendered with
 * dangerouslySetInnerHTML, so it is cleaned first: only plain formatting
 * tags survive, every attribute is dropped (no on* handlers, no style, no
 * href / src), and script-like elements are removed with their content.
 * Anything else is unwrapped so its text is kept.
 *
 * DOMParser builds an inert document — nothing in it runs or loads while it
 * is being cleaned.
 */

const ALLOWED = new Set([
  'P',
  'BR',
  'UL',
  'OL',
  'LI',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'H3',
  'H4',
  'H5',
  'H6',
  'SPAN',
  'DIV',
  'SMALL',
  'TABLE',
  'THEAD',
  'TBODY',
  'TR',
  'TD',
  'TH',
]);

const DROP_WITH_CONTENT = new Set([
  'SCRIPT',
  'STYLE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'TEMPLATE',
  'NOSCRIPT',
  'SVG',
  'MATH',
  'FORM',
  'INPUT',
  'BUTTON',
  'TEXTAREA',
  'SELECT',
  'LINK',
  'META',
]);

const escapeText = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const clean = (node) => {
  [...node.childNodes].forEach((child) => {
    if (child.nodeType === 3) return; // text
    if (child.nodeType !== 1) {
      child.remove(); // comments, processing instructions
      return;
    }
    const tag = child.tagName.toUpperCase();
    if (DROP_WITH_CONTENT.has(tag)) {
      child.remove();
      return;
    }
    clean(child);
    if (!ALLOWED.has(tag)) {
      child.replaceWith(...child.childNodes);
      return;
    }
    [...child.attributes].forEach((a) => child.removeAttribute(a.name));
  });
};

/** Supplier HTML -> HTML that is safe for dangerouslySetInnerHTML. '' for nothing. */
export const sanitizeHtml = (html) => {
  if (html === null || html === undefined) return '';
  const source = String(html);
  if (!source.trim()) return '';
  if (typeof DOMParser === 'undefined') return escapeText(source);
  const doc = new DOMParser().parseFromString(
    `<div>${source}</div>`,
    'text/html',
  );
  const root = doc.body.firstElementChild;
  if (!root) return '';
  clean(root);
  // "<ul>…</ul><p></p>" — drop the empty trailing paragraphs suppliers leave
  return (root.textContent || '').trim() ? root.innerHTML : '';
};

export default sanitizeHtml;
